<script>
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { fade, fly } from 'svelte/transition';

  import { authServer, vptServer, isAuthenticated, token, userUrn, userFilters, showDisclaimer, cohortFilters, userName, staffUrn } from './stores.js';
  import { setReferenceDataCohort, setReferenceDataSurveyAnswerLookupList, setReferenceDataSurveyQuestionConfiguration, 
      setReferenceDataEarlyRiskTabList, applyFilters, setReferenceDataRiskFactorTabList } from "./reference-data.js";
  import { referenceDataKeyworkerList, referenceDataStatusList, referenceDataEthnicityList, referenceDataNationalityList,
      referenceDataReligionList, referenceDataUnitList, referenceDataWingList, referenceDataCellStatusList, 
      referenceDataRegimeLevelList, referenceDataAgeRangeList } from "./reference-data";

  let welcomeName = "";
  let hasAuthToken = false;
  let password = "";
  let username = "";
  let authenticationFailed = false;
  let errorMessage = "";

  let showLogin = false;
  let showPlaceholder = true;
  let showLogos = true;
  let showAIMText = false;
  let isFormDisabled = false;

  const THREE_MONTHS_IN_MILLIS = 7772400000;

  onMount(() => {
    // Check to see whether valid authentication credentials are being passed with the HTTP request 
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('token')) {
      if (urlParams.has('token') && urlParams.has('name') && urlParams.has('urn') && urlParams.has('refresh')) {
        hasAuthToken = true;
        welcomeName = urlParams.get('name');
      } else {  
        authenticationFailed = true;
        errorMessage = "Invalid Login Credentials";
      }
    }

    let disclaimerViewed = localStorage.getItem("disclaimer-viewed");
    if (disclaimerViewed == null || Date.now() - parseInt(disclaimerViewed) > THREE_MONTHS_IN_MILLIS) {
      showDisclaimer.set(true);
    }

    getReferenceDataCohort();
    getReferenceDataSurveyAnswerLookupList();
    getReferenceDataSurveyQuestionConfiguration();
    getReferenceDataEarlyRiskTabList();
    getReferenceDataRiskFactorTabList();
  });

  async function getReferenceDataCohort() {
    await fetch(`${$vptServer}/reference-data/cohort`)
      .then(r => r.json())
      .then(data => {
        setReferenceDataCohort(data);
      });
  }

  async function getReferenceDataSurveyAnswerLookupList() {
    await fetch(`${$vptServer}/reference-data/survey-answer-lookup`)
    .then(r => r.json())
    .then(data => {
      setReferenceDataSurveyAnswerLookupList(data);
    });
  }

  async function getReferenceDataSurveyQuestionConfiguration() {
    await fetch(`${$vptServer}/reference-data/survey-question-configuration`)
    .then(r => r.json())
    .then(data => {
      setReferenceDataSurveyQuestionConfiguration(data);
    });
  }

  async function getReferenceDataEarlyRiskTabList() {
    await fetch(`${$vptServer}/reference-data/early-risk-tab`)
            .then(r => r.json())
            .then(data => {
              setReferenceDataEarlyRiskTabList(data);
            });
  }

  async function getReferenceDataRiskFactorTabList() {
    await fetch(`${$vptServer}/reference-data/risk-factor-tab`)
            .then(r => r.json())
            .then(data => {
              setReferenceDataRiskFactorTabList(data);
            });
  }

  const handleLogin = () => {
    if ($referenceDataEthnicityList.length == 0 && $referenceDataStatusList.length == 0
        && $referenceDataNationalityList.length == 0 && $referenceDataReligionList.length  == 0) {
      errorMessage = "There was a problem getting reference data. Please try refreshing.";
      failAuthentication("CUSTOM_ERROR_MESSAGE");
    } else {
      login ();
    }
  }

  async function login() {
    const encoded = btoa(username + ":" + password);
    
    await fetch(`${$authServer}/auth/upp/token/aim`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "Authorization": "Basic " + encoded
      }
    })
    .then(r => {
      if (!r.ok) {
        throw Error(r.status);
      }
      return r;
    })
    .then(r => r.json())
    .then(parsed => {
      if (parsed.accessToken) {
        isAuthenticated.set(true);
        token.set(parsed.accessToken);
        localStorage.setItem("refresh", parsed.refreshToken);
        userUrn.set(parsed.userUrn);
        userName.set(parsed.userId);
        getUserConfig();
      } else {
        isFormDisabled = false;
        errorMessage = "Invalid access token";
        throw Error("CUSTOM_ERROR_MESSAGE");
      }
    })
    .catch(error => {
      console.error(error);
      failAuthentication(error.message);
    });
  };

  const handleAutomaticLogin = () => {
    const urlParams = new URLSearchParams(window.location.search);
    isAuthenticated.set(true);
    token.set(urlParams.get('token'));
    localStorage.setItem("refresh", urlParams.get('refresh'));
    userUrn.set(urlParams.get('urn'));
    userName.set(urlParams.get('name'));
    getUserConfig();
  };

  async function getUserConfig() {
        await fetch(`${$vptServer}/user/${$userUrn}/configuration`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            return r;
        })
        .then(r => r.json())
        .then(data => {
          staffUrn.set(data.staffUrn);
          userFilters.set(data.filter);
          try {
            applyUserFilters(data.filter);
          } catch {
            console.error("Error getting user filters");
          }
          showLogin = false;
        })
        .catch(error => {
            console.error(error);
            return;
        });
    }

  function applyUserFilters(filterString) {
    let presetCohortFilters = [
      { name: "staffFilter", value: "" },
      { name: "statusFilter", value: "" },
      { name: "ethnicFilter", value: "" },
      { name: "nationalityFilter", value: "" },
      { name: "religionFilter", value: "" },
      { name: "unitFilter", value: "" },
      { name: "wingFilter", value: "" },
      { name: "cellStatusFilter", value: "" },
      { name: "regimeFilter", value: "" },
      { name: "ageRangeFilter", value: "" },
      { name: "earlyDaysFilter", value: "" }
    ];

    if (filterString) {
      let filterMap = filterString.replace('?','').split('&').map(filter => {
        return {name: (filter.slice(0, filter.indexOf('='))), value: decodeURIComponent((filter.slice((filter.indexOf('=') + 1))))};
      });

      filterMap.forEach(filter => {
        presetCohortFilters.find(e => e.name == filter.name).value = filter.value;

        if (filter.name === "statusFilter") {
          referenceDataStatusList.set(applyFilters(filter.value, $referenceDataStatusList, 'description'));
        } else if (filter.name === "ethnicFilter") {
          referenceDataEthnicityList.set(applyFilters(filter.value, $referenceDataEthnicityList, 'code'));
        } else if (filter.name === "nationalityFilter") {
          referenceDataNationalityList.set(applyFilters(filter.value, $referenceDataNationalityList, 'description'));
        } else if (filter.name === "religionFilter") {
          referenceDataReligionList.set(applyFilters(filter.value, $referenceDataReligionList, 'description'));
        } else if (filter.name === "unitFilter") {
          referenceDataUnitList.set(applyFilters(filter.value, $referenceDataUnitList, 'description'));
        } else if (filter.name === "wingFilter") {
          referenceDataWingList.set(applyFilters(filter.value, $referenceDataWingList, 'description'));
        } else if (filter.name === "cellStatusFilter") {
          referenceDataCellStatusList.set(applyFilters(filter.value, $referenceDataCellStatusList, 'vptCellStatusUrn'));
        } else if (filter.name === "regimeFilter") {
          referenceDataRegimeLevelList.set(applyFilters(filter.value, $referenceDataRegimeLevelList, 'level'));
        } else if (filter.name === "ageRangeFilter") {
          referenceDataAgeRangeList.set(applyFilters(filter.value, $referenceDataAgeRangeList, 'code'));
        } else if (filter.name === "staffFilter") {
          let staffUrns = filter.value.split(',');
          referenceDataKeyworkerList.set(staffUrns.map(e => {return {checked: true, staffUrn: parseInt(e)}}));
        }
      });
    }

    cohortFilters.set(presetCohortFilters);
  }

  function failAuthentication(error) {
    if (error != "CUSTOM_ERROR_MESSAGE") {
      if (error != "403" && error != "404") {
        errorMessage = "Server is not responding";
      } else {
        errorMessage = (error === "403") ? "Incorrect login details" : "Address not found";
      }
    }

    isFormDisabled = false;
    isAuthenticated.set(false);
    token.set("");
    password = "";
    authenticationFailed = true;
  }

  function navigateToDashboard() {
    if ($isAuthenticated) {
      navigate("/dashboard");
    }
  }
</script>

<style>
  .container-fluid {
    position: absolute;
    top: 0;
    bottom: 0;
    padding-top: 60px;
    background-color: white;
  }

  .h3 {
    font-size: 19px;
  }

  .aim-logo {
    width: 100%;
    text-align: center;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
    margin-top: auto;
  }

  .spinner-container {
    padding: 0;
    max-width: 250px;
    opacity: 15%;
  }

  .spinner-container #aim-icon, .spinner-container #aim-spinner {
    top: 200px;
    width: 250px;
  }

  .spinner-container #aim-spinner {
    animation-duration: 1s;
    animation-iteration-count: 1;
  }

  .aim-text-title img {
    height: 80px;
  }

  .aim-text-subtitle img {
    height: 20px;
    margin-top: 16px;
  }

  .auto-signin-message {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 290px;
  }

  .form-signin {
    width: 100%;
    max-width: 310px;
    height: 290px;
    padding: 0 15px;
    margin: auto;
  }

  .form-signin h1, .form-signin .alert {
    text-align: center;
  }

  .form-signin .form-control {
    position: relative;
    box-sizing: border-box;
    height: auto;
    padding: 12px;
    font-size: 14px;
  }

  .form-signin .form-control:focus {
    z-index: 2;
  }

  .form-signin input[type="text"] {
    margin-bottom: -1px;
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
  }

  .form-signin input[type="password"] {
    margin-bottom: 20px;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  .form-signin button {
    margin-bottom: 10px;
  }

  .placeholder {
    display: block;
    height: 290px;
  }

  .credits {
    width: 100%;
    max-width: 520px;
    margin-left: auto;
    margin-right: auto;
  }

  .credits p {
    text-align: center;
    margin: 10px 0 0 0;
    padding: 0 125px;
    font-size: 14px;
  }

  .logo-row {
    display: flex;
    justify-content: space-evenly;
    align-items: flex-start;
    padding: 10px 30px 18px 30px;
  } 

  #logo-u {
    height: 24px;
  }

  #logo-s {
    height: 30px;
    margin-top: 18px;
  }

  #logo-x {
    height: 36px;
    margin-top: 3px;
  }

  #logo-g {
    height: 30px;
    margin-top: 13px;
  }
</style>

<div class="container-fluid">

{#if showLogos}
  {#if !showAIMText}
    <div class="spinner-container" in:fade="{{ duration: 50 }}"
         out:fade="{{ delay:500, duration: 900 }}"
         on:introend="{() => {showAIMText = true}}">
      <img id="aim-icon" src="/assets/logo-aim-target.png" alt="AIM Logo" />
      <img id="aim-spinner" src="/assets/logo-radar-spinner.png" alt="AIM Logo" />
    </div>
  {/if}

  <div class="aim-logo">
    {#if showAIMText}
      <div class="aim-text-title"
        in:fade="{{ delay: 800, duration: 800 }}"
        out:fade="{{ duration: 800 }}">
        <img src="/assets/logo-aim-text.png" alt="AIM" />
      </div>
    {/if}

    <div class="aim-text-subtitle">
      <img id="logo-alert" src="/assets/logo-alert.png" alt="Alert" in:fade="{{ delay: 2000, duration: 600 }}" out:fade="{{ duration: 800 }}"/>
      <img id="logo-invervene" src="/assets/logo-intervene.png" alt="Intervene" in:fade="{{ delay: 2800, duration: 600 }}" out:fade="{{ duration: 800 }}"/>
      <img id="logo-monitor" src="/assets/logo-monitor.png" alt="Monitor" in:fade="{{ delay: 3600, duration: 600 }}" out:fade="{{ duration: 800 }}"/>
    </div>
  </div>
{/if}

{#if showLogin}
  {#if hasAuthToken}
  <div class="auto-signin-message mb-5">
    <p in:fade="{{ duration: 800 }}"
       on:introend="{() => {handleAutomaticLogin();}}"
       out:fade="{{ duration: 500 }}"
       on:outroend="{() => {showPlaceholder = true; showLogos = false}}">
        Welcome {welcomeName}, automatically logging you in...
    </p>
  </div>
  {:else}
  
  <form in:fade="{{ duration: 800 }}"
        out:fade="{{ duration: 500 }}"
        on:submit|preventDefault={() => {isFormDisabled = true; handleLogin();}}
        on:outroend="{() => {showPlaceholder = true; showLogos = false}}"
        class="form-signin mt-5">

    <h1 class="h3 mb-3 font-weight-normal text-muted">Please sign in</h1>
    
    <label for="username" class="sr-only">Username</label>
    <input on:keydown="{() => authenticationFailed = false}" type="text" bind:value="{username}" id="username"
      class="form-control" placeholder="Username" autocomplete="username" required />
    
    <label for="inputPassword" class="sr-only">Password</label>
    <input on:keydown="{() => authenticationFailed = false}" type="password" bind:value="{password}" id="inputPassword"
      class="form-control" placeholder="Password" autocomplete="current-password" required />

    <button class="btn btn-lg btn-primary btn-block" type="submit" disabled={isFormDisabled}>Sign in</button>

    {#if authenticationFailed}
      <div out:fade={{ duration: 2000 }} class="alert alert-danger" role="alert">{errorMessage}</div>
    {/if}
  </form>
  {/if}
{/if}

{#if showPlaceholder}
  <div class="placeholder mt-5" />
{/if}

{#if showLogos}
  <div class="credits"
    in:fly="{{ y: -200, delay: 3800, duration: 1200 }}"
    out:fly="{{ y: -200, duration: 800 }}"
    on:introend="{() => {showPlaceholder = false; showLogin = true}}"
    on:outroend="{navigateToDashboard}">

    <div class="logo-row">
      <a href="https://www.unilink.com/" target="_blank" rel="noreferrer"><img id="logo-u" src="/assets/logo-u.png" alt="Unilink Logo" /></a>
    </div>

    <p class="text-muted">Partnered development of an original Serco idea and proof of concept.</p>
    
    <div class="logo-row">
      <div in:fade="{{ delay:4500, duration: 800 }}">
        <a href="https://www.serco.com/" target="_blank" rel="noreferrer"><img id="logo-s" src="/assets/logo-s.png" alt="Serco Logo" /></a>
      </div>
      <div in:fade="{{ delay:5300, duration: 800 }}">
        <a href="https://www.g4s.com/" target="_blank" rel="noreferrer"><img id="logo-g" src="/assets/logo-g.png" alt="G4S Logo" /></a>
      </div>
      <div in:fade="{{ delay:6100, duration: 800 }}">
        <a href="https://www.sodexo.com/" target="_blank" rel="noreferrer"><img id="logo-x" src="/assets/logo-x.png" alt="Sodexo Logo" /></a>
      </div>
    </div>
  </div>
{/if}

</div>